import { useEffect, useMemo, useState } from "react";
import { Download, ImageOff, Shield, ShieldOff, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { rpc } from "../../lib/rpc";
import { API_BASE } from "../../lib/api";

const PURPOSES = [
    "article_image",
    "asset_identity",
    "serial_number",
    "delivery_note",
    "damage",
    "disposal_evidence",
    "inventory_difference",
    "location_orientation",
    "unknown_item",
    "return_condition",
    "maintenance",
    "qr_problem",
    "other",
];

/**
 * PhotoGallery -- Grid mit Thumbnails, Lightbox, optional Admin-Delete.
 * Props:
 *   entityType / entityId -- Filter (wenn gesetzt). Sonst zeigt alle.
 *   isAdmin -- ob Soft-Delete mit Grund verfügbar ist
 *   showFilters -- Default true
 *   initialPurpose -- preset filter
 */
export function PhotoGallery({
    entityType,
    entityId,
    isAdmin = false,
    showFilters = true,
    initialPurpose = null,
    testId = "photo-gallery",
    refreshKey = 0,
}) {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [purposeFilter, setPurposeFilter] = useState(initialPurpose || "all");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (entityType) params.set("entity_type", entityType);
        if (entityId) params.set("entity_id", entityId);
        if (purposeFilter !== "all") params.set("purpose", purposeFilter);
        const qs = params.toString();
        rpc.get(`/photos${qs ? "?" + qs : ""}`).then((r) => {
            setLoading(false);
            if (r.ok) setPhotos(r.data || []);
        });
    };

    useEffect(load, [entityType, entityId, purposeFilter, refreshKey]);

    const filtered = useMemo(() => {
        if (!search) return photos;
        const s = search.toLowerCase();
        return photos.filter(
            (p) =>
                (p.comment || "").toLowerCase().includes(s) ||
                (p.purpose || "").toLowerCase().includes(s) ||
                (p.entity_type || "").toLowerCase().includes(s)
        );
    }, [photos, search]);

    const thumbUrl = (p) => `${API_BASE}/photos/${p.id}/thumb`;
    const fileUrl = (p) => `${API_BASE}/photos/${p.id}/file`;

    // #136 Befund 3: Dialog mit Feld-Validierung statt window.prompt.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteReason, setDeleteReason] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const softDelete = async (event) => {
        event.preventDefault();
        if (deleteReason.trim().length < 3) {
            setDeleteError("Mindestens 3 Zeichen.");
            return;
        }
        const res = await rpc.post(`/photos/${deleteTarget.id}/soft-delete`, { reason: deleteReason.trim() });
        if (!res.ok) return toast.error(res.error);
        toast.success("Foto soft-deleted.");
        setDeleteTarget(null);
        setDeleteReason("");
        setDeleteError("");
        setSelected(null);
        load();
    };

    return (
        <div className="flex flex-col gap-3" data-testid={testId}>
            {showFilters ? (
                <div className="flex flex-wrap items-center gap-2">
                    <Input
                        placeholder="Suche Kommentar/Zweck..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-9 w-60"
                        data-testid={`${testId}-search`}
                    />
                    <Select value={purposeFilter} onValueChange={setPurposeFilter}>
                        <SelectTrigger className="h-9 w-56" data-testid={`${testId}-purpose-filter`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alle Zwecke</SelectItem>
                            {PURPOSES.map((p) => (
                                <SelectItem key={p} value={p}>
                                    {p}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <span className="ml-auto text-xs text-muted-foreground">
                        {loading ? "lädt..." : `${filtered.length} Foto(s)`}
                    </span>
                </div>
            ) : null}

            {filtered.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border bg-card/20 p-10 text-xs text-muted-foreground">
                    <ImageOff className="h-8 w-8 opacity-50" />
                    Keine Fotos vorhanden.
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {filtered.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setSelected(p)}
                            className="group relative aspect-square overflow-hidden rounded-md border border-border bg-card/40"
                            data-testid={`${testId}-tile-${p.id}`}
                        >
                            <img
                                src={thumbUrl(p)}
                                alt={p.purpose}
                                className="h-full w-full object-contain transition group-hover:scale-105"
                                loading="lazy"
                            />
                            {p.is_evidence ? (
                                <span className="absolute left-1 top-1 rounded bg-warning/90 px-1.5 py-0.5 text-xs font-semibold uppercase text-black">
                                    Evidence
                                </span>
                            ) : null}
                            <span className="absolute bottom-0 left-0 right-0 truncate bg-black/60 px-1 py-0.5 text-xs text-white">
                                {p.purpose}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-3xl" data-testid={`${testId}-lightbox`}>
                    {selected ? (
                        <div className="flex flex-col gap-3">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-sm">
                                    {selected.is_evidence ? (
                                        <Shield className="h-4 w-4 text-warning" />
                                    ) : (
                                        <ShieldOff className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    {selected.purpose} · {selected.entity_type}
                                </DialogTitle>
                            </DialogHeader>
                            <img
                                src={fileUrl(selected)}
                                alt={selected.purpose}
                                className="max-h-[60vh] w-full rounded-md object-contain"
                            />
                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                                <div>Upload: {selected.uploaded_by?.slice(0, 8) || "—"}</div>
                                <div>
                                    Datum:{" "}
                                    {selected.taken_at
                                        ? new Date(selected.taken_at).toLocaleString("de-DE")
                                        : new Date(selected.created_at).toLocaleString("de-DE")}
                                </div>
                                <div>
                                    Größe: {Math.round((selected.size_bytes || 0) / 1024)} KB
                                </div>
                                <div>
                                    {selected.width || "?"}×{selected.height || "?"} px
                                </div>
                            </div>
                            {selected.comment ? (
                                <div className="rounded-md bg-card/40 p-2 text-xs">
                                    <Label className="text-xs uppercase tracking-wider">Kommentar</Label>
                                    <div>{selected.comment}</div>
                                </div>
                            ) : null}
                            <div className="flex items-center justify-between gap-2">
                                <a
                                    href={fileUrl(selected)}
                                    download
                                    className="inline-flex h-9 items-center rounded-md border border-border px-3 text-xs hover:bg-card/60"
                                    data-testid={`${testId}-download`}
                                >
                                    <Download className="mr-1 h-4 w-4" /> Download
                                </a>
                                <div className="flex gap-2">
                                    {isAdmin ? (
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => { setDeleteTarget(selected); setDeleteReason(""); setDeleteError(""); }}
                                            data-testid={`${testId}-soft-delete`}
                                        >
                                            <Trash2 className="mr-1 h-4 w-4" /> Soft-Delete
                                        </Button>
                                    ) : null}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelected(null)}
                                    >
                                        <X className="mr-1 h-4 w-4" /> Schliessen
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
            <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
                <DialogContent data-testid={`${testId}-delete-dialog`}>
                    <DialogHeader><DialogTitle>Foto löschen (Soft-Delete)?</DialogTitle></DialogHeader>
                    <form onSubmit={softDelete} className="flex flex-col gap-3">
                        <p className="text-sm text-muted-foreground">
                            Das Foto wird ausgeblendet, bleibt aber im Audit nachvollziehbar.
                        </p>
                        <Label htmlFor={`${testId}-delete-reason`}>Lösch-Grund (mind. 3 Zeichen) *</Label>
                        <Input
                            id={`${testId}-delete-reason`}
                            autoFocus
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            aria-invalid={!!deleteError}
                            data-testid={`${testId}-delete-reason`}
                        />
                        {deleteError ? (
                            <p className="text-xs text-destructive" data-testid={`${testId}-delete-error`}>{deleteError}</p>
                        ) : null}
                        <Button type="submit" variant="destructive" data-testid={`${testId}-delete-submit`}>
                            Löschen
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
